import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Menu,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { Eye, Settings, Calendar } from "lucide-react";
import { dummyLedgerData } from "../../../../../dummyData";
import { NavLink } from "react-router-dom";

// Define default columns for the table
export const defaultColumns = [
  { id: "date", label: "Date", minWidth: 120, align: "center", required: true },
  { id: "type", label: "Type", minWidth: 100, align: "center", required: true },
  {
    id: "receiptNo",
    label: "Receipt/Bill No",
    minWidth: 150,
    align: "center",
    required: false,
  },
  {
    id: "txnId",
    label: "TxnID",
    minWidth: 120,
    align: "center",
    required: false,
  },
  {
    id: "account",
    label: "Account",
    minWidth: 150,
    align: "center",
    required: true,
  },
  {
    id: "debit",
    label: "Debit (₹)",
    minWidth: 150,
    align: "right",
    required: true,
  },
  {
    id: "credit",
    label: "Credit (₹)",
    minWidth: 150,
    align: "right",
    required: true,
  },
  {
    id: "balance",
    label: "Balance (₹)",
    minWidth: 180,
    align: "right",
    required: true,
  },
  {
    id: "narration",
    label: "Narration",
    minWidth: 200,
    align: "left",
    required: false,
  },
  {
    id: "action",
    label: "Action",
    minWidth: 100,
    align: "center",
    required: true,
  },
];

const LedgerTable = ({
  dateRange,
  onSettingsClick,
  anchorEl,
  onCloseSettings,
  visibleColumns,
  onColumnToggle,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [transactions] = useState(dummyLedgerData.transactions);

  // Filter columns based on visibility settings
  const displayColumns = visibleColumns
    ? defaultColumns.filter(column => visibleColumns[column.id])
    : defaultColumns;

  const formatCurrency = amount => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Filter data based on date range if provided
  const filteredData =
    dateRange && dateRange.from && dateRange.to
      ? transactions.filter(transaction => {
          const txnDate = new Date(
            transaction.date.split("/").reverse().join("-"),
          );
          const fromDate = new Date(dateRange.from);
          const toDate = new Date(dateRange.to);
          return txnDate >= fromDate && txnDate <= toDate;
        })
      : transactions;

  const displayedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Helper function to determine the navigation route based on transaction type
  const getNavigationRoute = transaction => {
    if (transaction.type === "Sales") {
      // Use a static route for Sales transactions that matches existing dummy data
      return `/orders/orderdetail/ord002`;
    } else if (transaction.type === "Receipt") {
      // Use a static route for Receipt transactions that matches existing dummy data
      return `/receipt/view/rcpt001`;
    }
    return "#"; // Default fallback
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      {/* Column Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseSettings}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 200,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-600">Show Columns</p>
        </div>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => onColumnToggle(column.id)}
            disabled={column.required}
            dense>
            <Checkbox
              checked={visibleColumns ? visibleColumns[column.id] : true}
              disabled={column.required}
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": {
                  color: "#5652B0",
                },
              }}
            />
            <span
              className={`text-sm ${column.required ? "text-gray-400" : ""}`}>
              {column.label}
              {column.required && " *"}
            </span>
          </MenuItem>
        ))}
      </Menu>

      <TableContainer
        sx={{ maxHeight: "calc(100vh - 350px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {displayColumns.map(column => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length} align="center">
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={40} />
                  </div>
                </TableCell>
              </TableRow>
            ) : displayedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length} align="center">
                  <div className="py-4 text-gray-500">
                    No transactions found
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              displayedData.map(transaction => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={transaction.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}>
                  {displayColumns.map(column => {
                    const cellValue = transaction[column.id];

                    if (column.id === "action") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          <div className="flex items-center justify-center">
                            <NavLink
                              to={getNavigationRoute(transaction)}
                              className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                              <Eye size={16} className="text-[#5652B0]" />
                            </NavLink>
                          </div>
                        </TableCell>
                      );
                    }

                    if (column.id === "account") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          <div
                            className={`px-3 py-1 rounded-full text-sm font-medium inline-block
                            ${
                              transaction.account === "Sale"
                                ? "bg-blue-100 text-blue-800"
                                : transaction.account === "Cash"
                                ? "bg-green-100 text-green-800"
                                : "bg-purple-100 text-purple-800"
                            }`}>
                            {transaction.account}
                          </div>
                        </TableCell>
                      );
                    }

                    if (column.id === "debit") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color:
                              transaction.debit > 0 ? "#EF4444" : "#4B5563",
                            fontWeight: transaction.debit > 0 ? "600" : "400",
                          }}
                          className="font-outfit">
                          {transaction.debit > 0
                            ? formatCurrency(transaction.debit)
                            : "-"}
                        </TableCell>
                      );
                    }

                    if (column.id === "credit") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color:
                              transaction.credit > 0 ? "#10B981" : "#4B5563",
                            fontWeight: transaction.credit > 0 ? "600" : "400",
                          }}
                          className="font-outfit">
                          {transaction.credit > 0
                            ? formatCurrency(transaction.credit)
                            : "-"}
                        </TableCell>
                      );
                    }

                    if (column.id === "balance") {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                            fontWeight: "500",
                          }}
                          className="font-outfit">
                          {formatCurrency(transaction.balance)}{" "}
                          {transaction.balanceType}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {cellValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
        }}
      />

      {isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </Paper>
  );
};

export default LedgerTable;
